/**
 * Which way are you actually facing?
 *
 * `ProductOverview.md` §6 names sensor variance across Android devices as the largest
 * engineering unknown, and it is right: phone magnetometers disagree with each other, with
 * themselves, and with reality — near cars, near speakers, near the metal railing of a
 * promenade, and for thirty seconds after the phone comes out of a pocket.
 *
 * There is a second heading available for free, and it is much better: **course over ground**.
 * Once you are walking, the GPS knows the direction you are travelling from the shape of your
 * own track, and no amount of magnetic interference touches it.
 *
 * The two are not interchangeable, which is why this is a blend rather than a swap:
 *
 *   standing still   course is meaningless (noise divided by nothing) — the compass is all there is
 *   walking          course is the truth, and the compass is a rumour
 *
 * The crossover is not instant either, because a needle that jumped every time somebody paused
 * at a kerb would be worse than either source alone.
 */

import { lerpAngle, normalizeDegrees, shortestAngleDelta } from '@beeline/shared';

/** Below this, course over ground is noise: you have not moved far enough to have a direction. */
const STILL_MPS = 0.5;

/** Above this, course is trusted outright. Roughly a brisk walk. */
const WALKING_MPS = 1.4;

/**
 * How far the compass may disagree with the course before it is treated as broken.
 *
 * If somebody is demonstrably walking north and the magnetometer insists they are facing east,
 * one of the two is wrong, and it is not the one derived from movement.
 */
const DISAGREEMENT_DEGREES = 60;

export type HeadingBasis = 'compass' | 'course' | 'blend';

export interface BlendedHeading {
  /** Degrees clockwise from true north, or null when nothing usable is available. */
  heading: number | null;
  /** Where it mostly came from. Surfaced in the debug panel, never hidden from the developer. */
  basis: HeadingBasis;
  /**
   * True when the compass and the course disagree badly enough that the compass is probably
   * being interfered with. The interface uses it to stop claiming a confident bearing.
   */
  suspect: boolean;
}

export interface HeadingInputs {
  /** The magnetometer's answer, already normalised to true north by `HeadingProvider`. */
  compass: number | null;
  /** The direction of travel, where the platform reports one. */
  course: number | null;
  /** Metres per second, where the platform reports it. */
  speed: number | null;
}

/**
 * How much to trust the course, from 0 (standing still) to 1 (walking).
 *
 * Ramped rather than switched, so pausing at a kerb fades the compass back in over a second or
 * two instead of snapping the needle to it.
 */
export function courseWeight(speed: number | null): number {
  if (speed === null || !Number.isFinite(speed) || speed <= STILL_MPS) return 0;
  if (speed >= WALKING_MPS) return 1;
  return (speed - STILL_MPS) / (WALKING_MPS - STILL_MPS);
}

/**
 * Combine the two into the one heading the arrow uses.
 *
 * Pure, so the rule can be tested without a phone, a car park or a magnet.
 */
export function blendHeading({ compass, course, speed }: HeadingInputs): BlendedHeading {
  const usableCourse = course !== null && Number.isFinite(course) ? normalizeDegrees(course) : null;
  const usableCompass =
    compass !== null && Number.isFinite(compass) ? normalizeDegrees(compass) : null;

  const weight = usableCourse === null ? 0 : courseWeight(speed);

  if (usableCompass === null) {
    // No compass at all. A moving device still has a heading; a still one has nothing, and
    // pretending otherwise would point an arrow from a number that means nothing.
    return usableCourse !== null && weight > 0
      ? { heading: usableCourse, basis: 'course', suspect: false }
      : { heading: null, basis: 'compass', suspect: false };
  }

  if (usableCourse === null || weight === 0) {
    return { heading: usableCompass, basis: 'compass', suspect: false };
  }

  const disagreement = Math.abs(shortestAngleDelta(usableCompass, usableCourse));
  const suspect = weight > 0.5 && disagreement > DISAGREEMENT_DEGREES;

  // When they disagree badly and we are definitely moving, the compass is the one that is
  // wrong. Blending towards a bad reading would drag the needle off the truth.
  if (suspect) return { heading: usableCourse, basis: 'course', suspect: true };

  return {
    heading: lerpAngle(usableCompass, usableCourse, weight),
    basis: weight >= 1 ? 'course' : 'blend',
    suspect: false,
  };
}
